<template>
  <div
    v-if="validQuestions && validQuestions.length > 0"
    class="customer-satisfaction"
    :class="
      ($dm('bg-white', 'dark:bg-slate-700'),
      {
        'pb-3': isRatingSubmitted,
      })
    "
    :style="{ borderColor: baseColor }"
    :dir="isRtl ? 'rtl' : 'ltr'"
  >
    <div v-for="(question, index) in validQuestions" :key="index">
      <csat-question
        :question="question"
        :index="index"
        :question-disabled="disabled"
        :message-content-attributes="messageContentAttributes"
        :answer="answers[question.id]"
        :survey="survey"
        @update="onUpdateAnswer"
      />
    </div>
    <form
      v-if="!isRatingSubmitted"
      class="feedback-form !p-0 mt-2 relative group"
      @submit.prevent="onSubmit()"
    >
      <button
        class="button w-full flex justify-center mt-5"
        :disabled="isButtonDisabled.length > 0"
        :style="{
          background: baseColor,
          borderColor: baseColor,
          color: textColor,
        }"
      >
        <spinner v-if="isUpdating" />
        <fluent-icon v-else icon="chevron-right" />
      </button>
      <div
        v-if="isButtonDisabled.length > 0"
        class="absolute bottom-full hidden group-hover:block text-sm bg-black-800 text-white m-2 p-2 rounded"
      >
        <ul class="m-0">
          <li v-for="(error, index) in isButtonDisabled" :key="index">
            {{ error }}
          </li>
        </ul>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import darkModeMixin from 'widget/mixins/darkModeMixin';
import { getContrastingTextColor } from '@chatwoot/utils';
import CsatQuestion from './CsatQuestion.vue';
import { buildCsatMessageContentAttributes } from '../constants/messages';

export default {
  components: {
    Spinner,
    FluentIcon,
    CsatQuestion,
  },
  mixins: [darkModeMixin],
  props: {
    messageContentAttributes: {
      type: Object,
      default: () => {},
    },
    messageId: {
      type: Number,
      default: null,
    },
    survey: {
      type: Object,
      default: () => ({}),
    },
    questions: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isInResponse: {
      type: Boolean,
      default: false,
    },
    isRtl: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isUpdating: false,
      answers: {},
      errors: '',
    };
  },
  computed: {
    ...mapGetters({ widgetColor: 'appConfig/getWidgetColor' }),
    validQuestions() {
      return this.questions.filter(
        question =>
          question?.title &&
          (![
            'dropdown_list',
            'combo_selection',
            'multiple_choice_markup',
            'single_choice_markup',
          ].includes(question.type) ||
            question.options?.length > 0)
      );
    },
    isRatingSubmitted() {
      return this.messageContentAttributes?.csat_survey_response?.answers;
    },
    isButtonDisabled() {
      if (this.disabled) return [this.$t('CSAT.RESPONSE_FORM.DISABLED_ERROR')];

      let errors = [];

      const mandatoryQuestionIds = this.validQuestions
        .filter(question => question.mandatory)
        .map(question => question.id);
      if (
        mandatoryQuestionIds?.find(
          id =>
            !this.answers[id] ||
            this.answers[id].length === 0 ||
            this.answers[id][0] === null ||
            this.answers[id][0] === undefined ||
            this.answers[id][0] === ''
        )
      ) {
        errors.push(this.$t('CSAT.RESPONSE_FORM.MANDATORY_QUESTION_ERROR'));
      }

      const emailQuestionIds = this.validQuestions
        .filter(question => question.type === 'email')
        .map(question => question.id);
      if (
        emailQuestionIds?.find(
          id =>
            (this.answers[id]?.[0] ?? '').length > 0 &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.answers[id]?.[0] ?? '')
        )
      ) {
        errors.push(this.$t('CSAT.RESPONSE_FORM.INVALID_EMAIL_ERROR'));
      }

      const phoneQuestionIds = this.validQuestions
        .filter(question => question.type === 'phone')
        .map(question => question.id);
      if (
        phoneQuestionIds?.find(
          id =>
            (this.answers[id]?.[0] ?? '').length > 0 &&
            !/^\+?[\d\s\-().]{7,15}$/.test(this.answers[id]?.[0] ?? '')
        )
      ) {
        errors.push(this.$t('CSAT.RESPONSE_FORM.INVALID_PHONE_ERROR'));
      } else if (
        phoneQuestionIds?.find(
          id =>
            (this.answers[id]?.[0] ?? '').length > 0 &&
            !this.answers[id]?.[0]?.startsWith('+')
        )
      ) {
        errors.push(this.$t('CSAT.RESPONSE_FORM.PHONE_MUST_START_WITH'));
      }

      return errors;
    },
    baseColor() {
      return this.widgetColor || '#4A90E2';
    },
    textColor() {
      return getContrastingTextColor(this.baseColor);
    },
  },

  mounted() {
    if (this.isRatingSubmitted) {
      const {
        csat_survey_response: { answers },
      } = this.messageContentAttributes;
      this.answers = answers;
    }
  },

  methods: {
    async onSubmit() {
      if (this.disabled) return;
      if (this.isInResponse) {
        this.$emit('submit-survey', this.answers);
        return;
      }

      this.isUpdating = true;
      try {
        await this.$store.dispatch('message/update', {
          submittedValues: buildCsatMessageContentAttributes(
            this.survey,
            this.validQuestions,
            this.answers
          ),
          messageId: this.messageId,
        });
      } catch (error) {
        // Ignore error
      } finally {
        this.isUpdating = false;
      }
    },
    onUpdateAnswer(question, answer) {
      if (
        answer.length === 0 ||
        answer[0] === null ||
        answer[0] === undefined ||
        answer[0] === ''
      ) {
        const { [question.id]: _removed, ...rest } = this.answers;
        this.answers = rest;
        return;
      }

      this.answers = {
        ...this.answers,
        [question.id]: answer,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~widget/assets/scss/variables.scss';
@import '~widget/assets/scss/mixins.scss';

.customer-satisfaction {
  @include light-shadow;

  border-bottom-left-radius: $space-smaller;
  border-radius: $space-small;
  border-top: $space-micro solid $color-woot;
  color: $color-body;
  display: inline-block;
  line-height: 1.5;
  margin-top: $space-smaller;
  width: 95%;

  .feedback-form {
    display: flex;

    input {
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
      border-bottom-left-radius: $space-small;
      border: 0;
      border-top: 1px solid $color-border;
      padding: $space-one;
      width: 100%;

      &::placeholder {
        color: $color-light-gray;
      }
    }

    .button {
      display: flex;
      appearance: none;
      border-bottom-left-radius: $space-small;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-bottom-right-radius: $space-small;
      font-size: $font-size-large;
      height: auto;
      margin-left: -1px;

      .spinner {
        display: block;
        padding: 0;
        height: auto;
        width: auto;
      }
    }
  }
}

.customer-satisfaction[dir='rtl'] {
  direction: rtl;
  text-align: right;

  .feedback-form {
    flex-direction: row-reverse;
  }
}

@media (prefers-color-scheme: dark) {
  .customer-satisfaction .feedback-form input {
    border-top: 1px solid var(--b-500);
  }
}
</style>
